// ============================================================================
// ROUTING SELECTOR — Searchable two-pane specialty + pathway picker
//
// Replaces the card-grid specialty/pack selector in GateView with a compact,
// searchable, list-based routing panel that scales to many specialties and packs.
//
// Layout:
//   Desktop (md+): [search] → [specialty list | pack list] → [footer: Continue]
//   Mobile:        [search] → [specialty list] → [pack list (stacked)] → [Continue]
//
// State model:
//   pendingSpecialtyId / pendingPackDocId — selections made in this panel but
//     NOT yet committed. Only "Continue" triggers the onCommit callback which
//     causes the parent to write to Firestore.
//
// Zero-PHI: no patient data involved. Only specialty/pack metadata.
// ============================================================================

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Search, ChevronRight, Check, Loader2, AlertCircle,
  Package, ListFilter, X, ArrowRight,
} from 'lucide-react';

// ── SpecialtyRow ──────────────────────────────────────────────────────────────

const SpecialtyRow = React.memo(({ id, spec, packCount, isSelected, onClick }) => (
  <button
    onClick={() => onClick(id)}
    className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-left text-sm transition-colors ${
      isSelected
        ? 'bg-emerald-500/15 border border-emerald-500/30 text-white'
        : 'bg-slate-800/40 border border-transparent hover:border-slate-600/50 hover:bg-slate-700/30 text-slate-300 hover:text-white'
    }`}
  >
    <span className="font-medium truncate">{spec.name}</span>
    <div className="flex items-center gap-1.5 flex-shrink-0">
      {packCount > 0 && (
        <span className={`text-xs px-1.5 py-0.5 rounded font-medium tabular-nums ${
          isSelected
            ? 'bg-emerald-500/20 text-emerald-400'
            : 'bg-slate-700/60 text-slate-500'
        }`}>
          {packCount}
        </span>
      )}
      <ChevronRight className={`w-3.5 h-3.5 ${isSelected ? 'text-emerald-400' : 'text-slate-600'}`} />
    </div>
  </button>
));
SpecialtyRow.displayName = 'SpecialtyRow';

// ── PackRow ───────────────────────────────────────────────────────────────────

const PackRow = React.memo(({ packDocId, pack, isSelected, onClick }) => {
  const reqCount = useMemo(
    () => (pack.fields || []).filter(f => f.required).length,
    [pack.fields],
  );
  const desc = pack.description || pack.metadata?.intent || '';

  return (
    <button
      onClick={() => onClick(packDocId)}
      className={`w-full flex items-start gap-3 px-3 py-3 rounded-lg text-left transition-colors ${
        isSelected
          ? 'bg-emerald-500/15 border border-emerald-500/30'
          : 'bg-slate-800/40 border border-transparent hover:border-slate-600/50 hover:bg-slate-700/30'
      }`}
    >
      {/* Radio indicator */}
      <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${
        isSelected ? 'border-emerald-500 bg-emerald-500' : 'border-slate-600'
      }`}>
        {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
      </div>

      {/* Pack details */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium leading-snug ${isSelected ? 'text-white' : 'text-slate-200'}`}>
          {pack.name || packDocId}
        </p>
        {desc && (
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2 leading-relaxed">{desc}</p>
        )}
        {reqCount > 0 && (
          <span className="inline-block mt-1.5 text-xs px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-500">
            {reqCount} required field{reqCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    </button>
  );
});
PackRow.displayName = 'PackRow';

// ── RoutingSelector ───────────────────────────────────────────────────────────

const RoutingSelector = ({
  /** [[specialtyId, specDoc], ...] — already role-filtered in parent */
  specialties = [],
  /** {[packDocId]: packDoc} — all packs for the tenant */
  rawPacks = {},
  /** Pre-seed from draft restore (uncommitted; parent uses null after Change) */
  initialSpecialtyId = null,
  initialPackDocId   = null,
  /** Called with (specialtyId, packDocId, rawPack) when user clicks Continue */
  onCommit,
  /** Draft write state from parent */
  draftSaving    = false,
  draftSaveError = null,
  /** True when restored draft references a pack that no longer exists */
  invalidDraft = false,
}) => {
  const [searchQuery,       setSearchQuery]       = useState('');
  const [pendingSpecialtyId, setPendingSpecialtyId] = useState(initialSpecialtyId);
  const [pendingPackDocId,   setPendingPackDocId]   = useState(initialPackDocId);

  // Re-seed if parent provides initial values after a mount (e.g. draft restore
  // that only sets specialtyId, not packDocId because pack validation failed).
  useEffect(() => {
    if (initialSpecialtyId && !pendingSpecialtyId) {
      setPendingSpecialtyId(initialSpecialtyId);
    }
  }, [initialSpecialtyId]); // eslint-disable-line

  // ── Derived: pack counts per specialty ─────────────────────────────────────

  const specPackCounts = useMemo(() => {
    const counts = {};
    Object.values(rawPacks).forEach(pack => {
      const sid = pack.specialtyId || pack.metadata?.specialtyId;
      if (sid) counts[sid] = (counts[sid] || 0) + 1;
    });
    return counts;
  }, [rawPacks]);

  // ── Derived: search-filtered specialties ───────────────────────────────────

  const filteredSpecialties = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return specialties;
    return specialties.filter(([sid, spec]) => {
      if ((spec.name || '').toLowerCase().includes(q)) return true;
      // Include specialty if any of its packs match the query
      return Object.values(rawPacks).some(p => {
        const pSid = p.specialtyId || p.metadata?.specialtyId;
        return pSid === sid && (p.name || '').toLowerCase().includes(q);
      });
    });
  }, [specialties, rawPacks, searchQuery]);

  // ── Derived: packs for selected specialty, search-filtered ─────────────────

  const packsForPending = useMemo(() => {
    if (!pendingSpecialtyId) return [];
    const q = searchQuery.trim().toLowerCase();
    return Object.entries(rawPacks).filter(([, pack]) => {
      const sid = pack.specialtyId || pack.metadata?.specialtyId;
      if (sid !== pendingSpecialtyId) return false;
      if (!q) return true;
      return (pack.name || '').toLowerCase().includes(q);
    });
  }, [rawPacks, pendingSpecialtyId, searchQuery]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleSpecialtyClick = useCallback((sid) => {
    setPendingSpecialtyId(sid);
    setPendingPackDocId(null); // reset pack on specialty change
  }, []);

  const handlePackClick = useCallback((packDocId) => {
    setPendingPackDocId(packDocId);
  }, []);

  const handleContinue = useCallback(() => {
    if (!pendingSpecialtyId || !pendingPackDocId || draftSaving) return;
    const rawPack = rawPacks[pendingPackDocId];
    if (!rawPack) return;
    onCommit(pendingSpecialtyId, pendingPackDocId, rawPack);
  }, [pendingSpecialtyId, pendingPackDocId, draftSaving, rawPacks, onCommit]);

  const canContinue = Boolean(pendingSpecialtyId && pendingPackDocId && !draftSaving);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-3">

      {/* Invalid draft warning */}
      {invalidDraft && (
        <div className="flex items-start gap-2.5 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-px" />
          <p className="text-xs text-amber-300 leading-relaxed">
            Your previous pathway selection is no longer available. Please select a new specialty and pathway to continue.
          </p>
        </div>
      )}

      {/* Draft save error */}
      {draftSaveError && (
        <div className="flex items-start gap-2.5 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-px" />
          <p className="text-xs text-red-400 leading-relaxed">{draftSaveError}</p>
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search specialty or pathway…"
          className="w-full bg-slate-800/60 border border-slate-700/60 rounded-lg pl-9 pr-8 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50 transition-colors"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-slate-500 hover:text-slate-300 transition-colors rounded"
            aria-label="Clear search"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Helper text */}
      {!pendingSpecialtyId && (
        <p className="text-xs text-slate-500 px-0.5">
          Choose a specialty first, then select a pathway.
        </p>
      )}

      {/* Two-pane / stacked selector */}
      <div className="flex flex-col md:flex-row gap-3">

        {/* ── LEFT: Specialty list ── */}
        <div className="md:w-56 md:flex-shrink-0">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 px-1">
            Specialties
          </p>
          <div className="space-y-1">
            {filteredSpecialties.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Package className="w-7 h-7 text-slate-700 mb-2" />
                <p className="text-sm text-slate-500">
                  {searchQuery ? 'No match' : 'No specialties available'}
                </p>
              </div>
            ) : (
              filteredSpecialties.map(([sid, spec]) => (
                <SpecialtyRow
                  key={sid}
                  id={sid}
                  spec={spec}
                  packCount={specPackCounts[sid] || 0}
                  isSelected={pendingSpecialtyId === sid}
                  onClick={handleSpecialtyClick}
                />
              ))
            )}
          </div>
        </div>

        {/* ── RIGHT: Pack list for selected specialty ── */}
        <div className={`flex-1 min-w-0 ${!pendingSpecialtyId ? 'hidden md:flex md:items-center md:justify-center' : ''}`}>
          {!pendingSpecialtyId ? (
            /* Desktop placeholder when no specialty selected */
            <div className="w-full flex flex-col items-center justify-center py-10 text-center">
              <ListFilter className="w-8 h-8 text-slate-700 mb-2.5" />
              <p className="text-sm text-slate-600">Select a specialty to see pathways</p>
            </div>
          ) : (
            <>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 px-1">
                Pathways
              </p>
              {packsForPending.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Package className="w-7 h-7 text-slate-700 mb-2" />
                  <p className="text-sm text-slate-500">
                    {searchQuery ? 'No pathways match your search' : 'No pathways available for this specialty'}
                  </p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {packsForPending.map(([packDocId, pack]) => (
                    <PackRow
                      key={packDocId}
                      packDocId={packDocId}
                      pack={pack}
                      isSelected={pendingPackDocId === packDocId}
                      onClick={handlePackClick}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Footer: Continue */}
      <div className="flex items-center justify-end pt-2 border-t border-slate-700/50">
        <button
          onClick={handleContinue}
          disabled={!canContinue}
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            canContinue
              ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
              : 'bg-slate-700/50 text-slate-500 cursor-not-allowed'
          }`}
        >
          {draftSaving ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
          ) : (
            <>Continue <ArrowRight className="w-4 h-4" /></>
          )}
        </button>
      </div>
    </div>
  );
};

export default RoutingSelector;
